import { isBoolean } from 'lodash'
import { ReactElement, forwardRef } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { AngleDownIcon } from 'components/Icons/AngleDown'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Label, OptionalLabel } from 'components/v2/Label'
import { ISelectContentProps, ISelectProps } from './types'

const SelectContainer = styled.label``

const SelectContent = styled.div<ISelectContentProps>`
  position: relative;
  align-items: center;
  display: flex;

  svg {
    height: 20px;
    width: 20px;
  }
`

export const StyledSelect = styled.select<{
  small?: boolean
  value?: unknown
  showError?: boolean
  noPadding?: boolean
  primary?: boolean
}>`
  appearance: none;
  background-color: ${({ primary }): string => (primary ? '#EAEAFF' : '#fff')};
  border: 1px solid
    ${({ showError, primary }): string => {
      if (showError) {
        return '#FF627B'
      }
      if (primary) {
        return '#8F8BFF'
      }
      return '#E3E3E3'
    }};
  border-radius: 4px;
  color: ${({ value }): string => (value || value === 0 || isBoolean(value) ? '#28265d' : '#cacad2')};
  padding: ${({ small, noPadding }): string => (noPadding ? '0px' : small ? '8px' : '12.5px 12px')};
  cursor: initial;
  flex: 1;
  font-size: ${({ small }): string => (small ? '14px' : '16px')};
  outline: none;
  width: 100%;

  &:disabled {
    background-color: #f0f0f0;
    border: 1px solid #e3e3e3;
    color: #b0b0b0;
    cursor: not-allowed;
  }

  &:hover {
    border: 1px solid #717171;
  }

  &:focus {
    border: 1px solid #717171;
  }

  option {
    color: black;

    &:disabled {
      color: #aaaaaa;
    }
  }
`

const StyledAngleDownIcon = styled(AngleDownIcon)<{ isOpen?: boolean }>`
  fill: #676879;
  height: 6px !important;
  transform: ${({ isOpen }): string => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: 0.2s;
  width: 10px !important;
  position: absolute;
  right: 12px;
`

const Select = forwardRef<HTMLSelectElement, ISelectProps>(
  (
    { className, label, touched, errors, isInvalid, small, noPadding, isOpen, optional, hideIcon, primary, ...props },
    ref
  ): ReactElement => {
    const showError = Boolean(touched && errors)

    return (
      <SelectContainer className={className}>
        {label && (
          <Label>
            {label}{' '}
            {optional && (
              <OptionalLabel>
                <FormattedMessage id="form.optional.label" defaultMessage="Optional" />
              </OptionalLabel>
            )}
          </Label>
        )}
        <SelectContent>
          <StyledSelect
            ref={ref}
            showError={showError || isInvalid}
            noPadding={noPadding}
            small={small}
            primary={primary}
            {...props}
          />
          {!props.disabled && !hideIcon ? <StyledAngleDownIcon isOpen={isOpen} /> : null}
        </SelectContent>
        {showError && <ErrorMessage>{errors}</ErrorMessage>}
      </SelectContainer>
    )
  }
)

Select.displayName = 'Select'

export { Select }
